import { HasChildren } from '@eppendorf/vnls-react-components';
import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { signInWithHostedUi } from '$shared/auth/auth-service';
import {
  savePostLoginRedirectPath,
  peekPostLoginRedirectPath,
} from '$shared/auth/post-login-redirect.utils';
import { useAuthContext } from '$shared/auth/use-auth-context';

import { Loader } from '$components/loader/loader';

interface Props {
  autoSignIn?: boolean;
  fallback?: React.ReactNode;
}

export function AuthGuard({
  children,
  autoSignIn,
  fallback,
}: HasChildren & Props): React.ReactNode {
  const { isLoading, isAuthenticated } = useAuthContext();
  const location = useLocation();

  useEffect(() => {
    const redirectPath = `${location.pathname}${location.search}`;

    const signIn = async () => {
      // Pass the target path as Cognito's OAuth `state` param (Amplify's `customState`),
      // so Amplify hands it back via the `customState` auth event once the Hosted UI
      // redirect completes (see auth-context-provider.tsx).
      await signInWithHostedUi(redirectPath);
    };

    if (!isAuthenticated) {
      if (!peekPostLoginRedirectPath()) {
        savePostLoginRedirectPath(redirectPath);
      }
      if (autoSignIn) signIn();
    }
  }, [isAuthenticated, location]);

  if (isLoading) {
    return <Loader />;
  }

  return isAuthenticated ? children : (fallback ?? '');
}
